import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ScenariosApi } from '../../../apis/ScenariosApi/ScenariosApi.ts';
import { useLowFrequencyRefreshQuery } from '../../../queryDefaults/baseQueries/useLowFrequencyRefreshQuery.tsx';
import {
  IScenarioWrite,
  IScenarioPatientSlotWrite,
  IScenarioQuestionnaireEntryWrite,
  IScenarioPhysiologicalEntryWrite,
  IScenarioLabTestEntryWrite,
  IScenarioJournalEntryWrite,
  IScenarioClinicalNoteEntryWrite,
  IScenarioApplyRequest,
} from '../Scenario.ts';

export const scenarioKeys = {
  all: ['scenarios'] as const,
  lists: () => [...scenarioKeys.all, 'list'] as const,
  detail: (id: number) => [...scenarioKeys.all, 'detail', id] as const,
};

export const useScenarios = () => {
  return useQuery({
    queryKey: scenarioKeys.lists(),
    queryFn: () => ScenariosApi.getAll(),
  });
};

export const useScenario = (id: number) => {
  return useQuery({
    queryKey: scenarioKeys.detail(id),
    queryFn: () => ScenariosApi.getById(id),
    enabled: !!id && id > 0,
  });
};

export const useCreateScenario = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (model: IScenarioWrite) => ScenariosApi.create(model),
    onSuccess: () => qc.invalidateQueries({ queryKey: scenarioKeys.lists() }),
  });
};

export const useUpdateScenario = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, model }: { id: number; model: IScenarioWrite }) =>
      ScenariosApi.update(id, model),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: scenarioKeys.lists() });
      qc.invalidateQueries({ queryKey: scenarioKeys.detail(id) });
    },
  });
};

export const useDeleteScenario = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => ScenariosApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: scenarioKeys.lists() }),
  });
};

export const useAddScenarioQuestionnaire = (scenarioId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      questionnaireId,
      orderIndex,
    }: {
      questionnaireId: number;
      orderIndex: number;
    }) =>
      ScenariosApi.addQuestionnaire(scenarioId, questionnaireId, orderIndex),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: scenarioKeys.detail(scenarioId) }),
  });
};

export const useRemoveScenarioQuestionnaire = (scenarioId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (questionnaireId: number) =>
      ScenariosApi.removeQuestionnaire(scenarioId, questionnaireId),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: scenarioKeys.detail(scenarioId) }),
  });
};

export const useAddScenarioJournal = (scenarioId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      journalId,
      orderIndex,
    }: {
      journalId: number;
      orderIndex: number;
    }) => ScenariosApi.addJournal(scenarioId, journalId, orderIndex),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: scenarioKeys.detail(scenarioId) }),
  });
};

export const useRemoveScenarioJournal = (scenarioId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (journalId: number) =>
      ScenariosApi.removeJournal(scenarioId, journalId),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: scenarioKeys.detail(scenarioId) }),
  });
};

export const useAddScenarioClinicalNoteType = (scenarioId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      noteTypeID,
      questionnaireID,
      orderIndex,
    }: {
      noteTypeID: number;
      questionnaireID: number | null;
      orderIndex: number;
    }) =>
      ScenariosApi.addClinicalNoteType(
        scenarioId,
        noteTypeID,
        questionnaireID,
        orderIndex,
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: scenarioKeys.detail(scenarioId) }),
  });
};

export const useRemoveScenarioClinicalNoteType = (scenarioId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (linkId: number) =>
      ScenariosApi.removeClinicalNoteType(scenarioId, linkId),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: scenarioKeys.detail(scenarioId) }),
  });
};

export const useCreateScenarioSlot = (scenarioId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (model: IScenarioPatientSlotWrite) =>
      ScenariosApi.createSlot(scenarioId, model),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: scenarioKeys.detail(scenarioId) }),
  });
};

export const useUpdateScenarioSlot = (scenarioId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      slotId,
      model,
    }: {
      slotId: number;
      model: IScenarioPatientSlotWrite;
    }) => ScenariosApi.updateSlot(slotId, model),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: scenarioKeys.detail(scenarioId) }),
  });
};

export const useDeleteScenarioSlot = (scenarioId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slotId: number) => ScenariosApi.deleteSlot(slotId),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: scenarioKeys.detail(scenarioId) }),
  });
};

export const useScenarioLookups = () =>
  useLowFrequencyRefreshQuery({
    queryKey: ['scenario-lookups'],
    queryFn: () => ScenariosApi.getLookups(),
  });

export const useUnitsForPhysiologicalType = (
  physiologicalTypeId: number | null,
) =>
  useLowFrequencyRefreshQuery({
    queryKey: ['scenario-physiological-units', physiologicalTypeId],
    queryFn: () =>
      ScenariosApi.getUnitsForPhysiologicalType(physiologicalTypeId!),
    enabled: !!physiologicalTypeId,
  });

const invalidateDetail = (
  qc: ReturnType<typeof useQueryClient>,
  scenarioId: number,
) => qc.invalidateQueries({ queryKey: scenarioKeys.detail(scenarioId) });

export const useCreateQuestionnaireEntry = (scenarioId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      slotId,
      model,
    }: {
      slotId: number;
      model: IScenarioQuestionnaireEntryWrite;
    }) => ScenariosApi.createQuestionnaireEntry(slotId, model),
    onSuccess: () => invalidateDetail(qc, scenarioId),
  });
};
export const useUpdateQuestionnaireEntry = (scenarioId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      entryId,
      model,
    }: {
      entryId: number;
      model: IScenarioQuestionnaireEntryWrite;
    }) => ScenariosApi.updateQuestionnaireEntry(entryId, model),
    onSuccess: () => invalidateDetail(qc, scenarioId),
  });
};
export const useDeleteQuestionnaireEntry = (scenarioId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entryId: number) =>
      ScenariosApi.deleteQuestionnaireEntry(entryId),
    onSuccess: () => invalidateDetail(qc, scenarioId),
  });
};

export const useCreatePhysiologicalEntry = (scenarioId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      slotId,
      model,
    }: {
      slotId: number;
      model: IScenarioPhysiologicalEntryWrite;
    }) => ScenariosApi.createPhysiologicalEntry(slotId, model),
    onSuccess: () => invalidateDetail(qc, scenarioId),
  });
};
export const useUpdatePhysiologicalEntry = (scenarioId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      entryId,
      model,
    }: {
      entryId: number;
      model: IScenarioPhysiologicalEntryWrite;
    }) => ScenariosApi.updatePhysiologicalEntry(entryId, model),
    onSuccess: () => invalidateDetail(qc, scenarioId),
  });
};
export const useDeletePhysiologicalEntry = (scenarioId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entryId: number) =>
      ScenariosApi.deletePhysiologicalEntry(entryId),
    onSuccess: () => invalidateDetail(qc, scenarioId),
  });
};

export const useCreateLabTestEntry = (scenarioId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      slotId,
      model,
    }: {
      slotId: number;
      model: IScenarioLabTestEntryWrite;
    }) => ScenariosApi.createLabTestEntry(slotId, model),
    onSuccess: () => invalidateDetail(qc, scenarioId),
  });
};
export const useUpdateLabTestEntry = (scenarioId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      entryId,
      model,
    }: {
      entryId: number;
      model: IScenarioLabTestEntryWrite;
    }) => ScenariosApi.updateLabTestEntry(entryId, model),
    onSuccess: () => invalidateDetail(qc, scenarioId),
  });
};
export const useDeleteLabTestEntry = (scenarioId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entryId: number) => ScenariosApi.deleteLabTestEntry(entryId),
    onSuccess: () => invalidateDetail(qc, scenarioId),
  });
};

export const useCreateJournalEntry = (scenarioId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      slotId,
      model,
    }: {
      slotId: number;
      model: IScenarioJournalEntryWrite;
    }) => ScenariosApi.createJournalEntry(slotId, model),
    onSuccess: () => invalidateDetail(qc, scenarioId),
  });
};
export const useUpdateJournalEntry = (scenarioId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      entryId,
      model,
    }: {
      entryId: number;
      model: IScenarioJournalEntryWrite;
    }) => ScenariosApi.updateJournalEntry(entryId, model),
    onSuccess: () => invalidateDetail(qc, scenarioId),
  });
};
export const useDeleteJournalEntry = (scenarioId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entryId: number) => ScenariosApi.deleteJournalEntry(entryId),
    onSuccess: () => invalidateDetail(qc, scenarioId),
  });
};

export const useCreateClinicalNoteEntry = (scenarioId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      slotId,
      model,
    }: {
      slotId: number;
      model: IScenarioClinicalNoteEntryWrite;
    }) => ScenariosApi.createClinicalNoteEntry(slotId, model),
    onSuccess: () => invalidateDetail(qc, scenarioId),
  });
};
export const useUpdateClinicalNoteEntry = (scenarioId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      entryId,
      model,
    }: {
      entryId: number;
      model: IScenarioClinicalNoteEntryWrite;
    }) => ScenariosApi.updateClinicalNoteEntry(entryId, model),
    onSuccess: () => invalidateDetail(qc, scenarioId),
  });
};
export const useDeleteClinicalNoteEntry = (scenarioId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entryId: number) =>
      ScenariosApi.deleteClinicalNoteEntry(entryId),
    onSuccess: () => invalidateDetail(qc, scenarioId),
  });
};

export const useSearchPatientUsers = (search: string) =>
  useQuery({
    queryKey: ['scenario-user-search', search],
    queryFn: () => ScenariosApi.searchUsers(search),
    enabled: search.length >= 2,
  });

export const useApplyScenario = (scenarioId: number) =>
  useMutation({
    mutationFn: (request: IScenarioApplyRequest) =>
      ScenariosApi.apply(scenarioId, request),
  });

export const useImportScenarioQuestionnaire = (scenarioId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) =>
      ScenariosApi.importQuestionnaire(scenarioId, file),
    onSuccess: (result) => {
      if (!result.unresolved || result.unresolved.length === 0)
        invalidateDetail(qc, scenarioId);
    },
  });
};

export const useImportScenarioJournal = (scenarioId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => ScenariosApi.importJournal(scenarioId, file),
    onSuccess: (result) => {
      if (!result.unresolved || result.unresolved.length === 0)
        invalidateDetail(qc, scenarioId);
    },
  });
};

export const useImportScenarioPhysiological = (scenarioId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => ScenariosApi.importPhysiological(scenarioId, file),
    onSuccess: (result) => {
      if (!result.unresolved || result.unresolved.length === 0)
        invalidateDetail(qc, scenarioId);
    },
  });
};

export const useImportScenarioLifeEvent = (scenarioId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => ScenariosApi.importLifeEvent(scenarioId, file),
    onSuccess: (result) => {
      if (!result.unresolved || result.unresolved.length === 0)
        invalidateDetail(qc, scenarioId);
    },
  });
};

export const useImportScenarioDocument = (scenarioId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => ScenariosApi.importDocument(scenarioId, file),
    onSuccess: (result) => {
      if (!result.unresolved || result.unresolved.length === 0)
        invalidateDetail(qc, scenarioId);
    },
  });
};

export const useImportScenarioClinicalNote = (scenarioId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => ScenariosApi.importClinicalNote(scenarioId, file),
    onSuccess: (result) => {
      if (!result.unresolved || result.unresolved.length === 0)
        invalidateDetail(qc, scenarioId);
    },
  });
};

export const useImportScenarioPrescription = (scenarioId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => ScenariosApi.importPrescription(scenarioId, file),
    onSuccess: (result) => {
      if (!result.unresolved || result.unresolved.length === 0)
        invalidateDetail(qc, scenarioId);
    },
  });
};
